import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SearchX, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import Nav, { scrollTo } from "@/components/Nav";
import ProductCard from "@/components/ProductCard";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CheckoutModal from "@/components/CheckoutModal";
import WhatsAppButton from "@/components/WhatsAppButton";

export default function SearchPage() {
  const { t, isAr } = useLanguage();
  const { products, categories } = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim();

  const results = useMemo(() => {
    if (!q) return [];
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return products.filter((p) => {
      const cat = categories.find((c) => c.id === p.category);
      const sub = cat?.subs?.find((s) => s.id === p.subcategory);
      const hay = [p.name, p.name_ar, p.desc, p.desc_ar, p.subcategory, sub?.name, sub?.name_ar, cat?.name, cat?.name_ar]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [q, products, categories]);

  return (
    <>
      <Nav />
      <main className="mx-auto max-w-7xl px-4 pb-10 pt-[132px] md:pt-[124px] lg:px-8" data-testid="search-page">
        <nav className="flex items-center gap-1 text-[11px] text-muted" data-testid="search-breadcrumb">
          <Link to="/" className="transition-colors hover:text-link">{t("pdp_home")}</Link>
          <ChevronRight className="h-3 w-3 rtl:-scale-x-100" />
          <span className="font-semibold text-brandink">{t("search_results")}</span>
        </nav>

        <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
          <h1 className="text-xl font-extrabold text-brandink md:text-2xl" data-testid="search-title">
            {t("search_results")} “{q}”
          </h1>
          <p className="text-sm text-muted" data-testid="search-count">
            {results.length} {t("products_count")}
          </p>
        </div>

        {results.length === 0 ? (
          <div className="col-span-full mt-6 flex flex-col items-center rounded-lg border border-line bg-white py-16" data-testid="search-empty">
            <SearchX className="h-10 w-10 text-brand" />
            <p className="mt-4 text-base font-bold text-brandink">{t("search_empty")}</p>
            <p className="mt-1 text-sm text-muted">{t("search_empty_hint")}</p>
            <Link
              to="/"
              onClick={() => setTimeout(() => scrollTo("#products"), 60)}
              data-testid="search-browse-all"
              className="mt-5 rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-brandink transition-transform hover:scale-105"
            >
              {t("shop_all")}
            </Link>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" data-testid="search-results">
            {results.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </main>
      <Footer />
      <CartDrawer />
      <CheckoutModal />
      <WhatsAppButton />
    </>
  );
}
